<template>
  <div>
    <b-form action="">
      <b-form-group
        :label="`${$t('auction.prize_expiry')}:`"
        label-cols="9"
      >
        <b-input-group :append="$t('common.days')">
          <!-- PRIZE EXPIRY -->
          <b-form-input
            v-model="form.prizeExpiry"
            type="number"
            :min="0"
            :state="expiryValid"
            number
            @input="validateExpiry"
          />
        </b-input-group>
      </b-form-group>
      <b-form-group
        :label="`${$t('auction.duration')}:`"
        label-cols="3"
        :state="durationValid"
        :invalid-feedback="$t('auction.wrong_date')"
      >
        <b-form-group
          :label="$t('common.start')"
          label-cols="3"
          label-size="sm"
          label-align="right"
        >
          <b-input-group>
            <!-- AUCTION ACTIVE FROM -->
            <b-form-input
              v-model="duration.activeFromDate"
              class="mr-2"
              type="date"
              @input="validateDuration"
            />
            <a-time-picker
              v-model="duration.activeFromTime"
              :use12hours="false"
              placeholder="HH:mm"
              format="HH:mm"
              value-format="HH:mm"
              :minute-step="60"
              @change="validateDuration"
            />
          </b-input-group>
        </b-form-group>
        <b-form-group
          :label="$t('common.end')"
          label-cols="3"
          label-size="sm"
          label-align="right"
        >
          <b-input-group>
            <!-- AUCTION ACTIVE TO -->
            <b-form-input
              v-model="duration.activeToDate"
              class="mr-2"
              type="date"
              @input="validateDuration"
            />
            <a-time-picker
              v-model="duration.activeToTime"
              :use12hours="false"
              placeholder="HH:mm"
              format="HH:mm"
              value-format="HH:mm"
              :minute-step="60"
              @change="validateDuration"
            />
          </b-input-group>
        </b-form-group>
      </b-form-group>
      <b-form-group
        :label="`${$t('auction.start_price')}:`"
        label-cols="7"
      >
        <!-- START PRICE -->
        <b-input-group :append="$t('common.UZS')">
          <b-form-input
            v-model="form.startPrice"
            type="number"
            min="0"
            step="1000"
            number
            :state="startPriceValid"
            @input="validateStartPrice"
          />
        </b-input-group>
      </b-form-group>
      <b-form-group
        :label="`${$t('auction.min_final_price')}:`"
        :description="$t('common.not_required')"
        label-cols="7"
      >
        <!-- MIN FINAL PRICE -->
        <b-input-group :append="$t('common.UZS')">
          <b-form-input
            v-model="form.minFinalPrice"
            type="number"
            min="0"
            step="1000"
            number
            @input="handleChange"
          />
        </b-input-group>
      </b-form-group>
      <b-form-group
        :label="`${$t('auction.min_price_step')}:`"
        label-cols="7"
      >
        <!-- MIN BID STEP -->
        <b-input-group :append="$t('common.UZS')">
          <b-form-input
            v-model="form.minBidStep"
            type="number"
            min="0"
            step="1000"
            :formatter="onlyThousandsFormatter"
            :lazy-formatter="true"
            :state="minBidStepValid"
            number
            @input="validateBidStep"
          />
        </b-input-group>
      </b-form-group>
    </b-form>
  </div>
</template>

<script>
import dayjs from 'dayjs'

export default {
  name: 'CommonAuctionSetup',
  model: {
    prop: 'form',
    event: 'change',
  },
  props: {
    auctionSettings: {
      type: Object,
      default() {
        return {
          prizeExpiry: null,
          activeFrom: null,
          activeTo: null,
          startPrice: null,
          minFinalPrice: null,
          minBidStep: null,
          isFormValid: false,

        }
      },
    },
  },
  data() {
    return {
      form: {
        prizeExpiry: null,
        activeFrom: null,
        activeTo: null,
        startPrice: null,
        minFinalPrice: null,
        minBidStep: null,
        isFormValid: false,
      },

      duration: {
        activeFromDate: '',
        activeFromTime: '',
        activeToDate: '',
        activeToTime: '',
      },

      expiryValid: null,
      durationValid: null,
      startPriceValid: null,
      minBidStepValid: null,
    }
  },

  watch: {
    auctionSettings: {
      handler(newVal) {
        this.form = newVal
        this.displayDuration(newVal)
      },
    },
  },

  methods: {
    handleChange() {
      this.form.isFormValid = this.validateForm()
      this.$emit('change', this.form)
    },

    validateExpiry() {
      this.expiryValid = this.form.prizeExpiry > 0
      this.handleChange()
    },

    validateStartPrice() {
      this.startPriceValid = this.form.startPrice > 0
      this.handleChange()
    },

    validateBidStep() {
      this.minBidStepValid = this.form.minBidStep > 0
      this.handleChange()
    },

    validateDuration() {
      this.handleDuration()
      const today = dayjs().valueOf()
      this.durationValid = this.form.activeFrom > today &&
        this.form.activeTo > this.form.activeFrom
      this.handleChange()
    },

    validateForm() {
      return (this.expiryValid &&
        this.durationValid &&
        this.startPriceValid &&
        this.minBidStepValid)
    },

    disableMinutes() {
      const res = []
      for (let i = 0; i <= 59; i++) {
        res.push(i)
      }
      return res
    },

    onlyThousandsFormatter(value) {
      return Math.floor(value / 1000) * 1000
    },

    displayDuration(form) {
      this.duration.activeFromDate = form.activeFrom
        ? dayjs(form.activeFrom).format('YYYY-MM-DD') : ''
      this.duration.activeFromTime = form.activeFrom
        ? dayjs(form.activeFrom).format('HH') : ''
      this.duration.activeFromDate = form.activeFrom
        ? dayjs(form.activeFrom).format('YYYY-MM-DD') : ''
      this.duration.activeFromTime = form.activeFrom
        ? dayjs(form.activeFrom).format('HH') : ''
    },

    handleDuration() {
      const customParseFormat = require('dayjs/plugin/customParseFormat')
      dayjs.extend(customParseFormat)

      function parseDate(date, time) {
        let res
        const parsedDate = dayjs(date, 'YYYY-MM-DD')
        const parsedTime = dayjs(time, 'HH:mm')
        if (isNaN(parsedDate.valueOf()) && parsedTime.valueOf() > 0) {
          res = dayjs().hour(parsedTime.hour()).minute(parsedTime.minute()).startOf('minute').valueOf()
        } else if (parsedDate.valueOf() > 0 && isNaN(parsedTime.valueOf())) {
          res = parsedDate.hour(12).startOf('hour').valueOf()
        } else if (parsedDate.valueOf() > 0 && parsedTime.valueOf() > 0) {
          res = parsedDate.hour(parsedTime.hour()).minute(parsedTime.minute()).startOf('minute').valueOf()
        } else {
          res = null
        }
        return res
      }

      this.form.activeFrom = parseDate(this.duration.activeFromDate, this.duration.activeFromTime)
      this.form.activeTo = parseDate(this.duration.activeToDate, this.duration.activeToTime)
    },
  },
}
</script>
